import type { FilterDefinition } from "@/shared/components/filters/FilterDefinition";
import type {
  Filter,
  FilterMatchMode,
} from "@/shared/components/filters/FilterSchema";
import { filterToCondition } from "@/shared/components/filters/filterToCondition";
import type { Condition } from "@/types";

/**
 * Compose the wire `condition` for a query by joining a route-enforced
 * `scope` with the user's `userFilters` under an outer `AND`:
 *
 * 1. Both groups empty → `undefined`.
 * 2. Exactly one group populated → that group's `Condition` bare.
 * 3. Both populated → `{ operator: "AND", conditions: [scope, user] }`.
 *
 * `matchMode` governs only how `userFilters` relate to each other; the
 * outer wrap stays `AND` so it cannot dissolve the scope. On a Sessions
 * tab pinned to `dataset.dataset_id = ds_abc`, this is what prevents the
 * user picking `OR` match mode from surfacing sessions outside that
 * dataset.
 *
 * `definitions` are the ones the filters were built against; see
 * `FilterToConditionOptions.definitions` for when a caller must pass them.
 */
export function composeFilters(
  scope: Filter[],
  userFilters: Filter[],
  matchMode: FilterMatchMode,
  definitions: readonly FilterDefinition[] = [],
): Condition | undefined {
  const scopeCondition = filterToCondition(scope, {
    matchMode: "AND",
    definitions,
  });
  const userCondition = filterToCondition(userFilters, {
    matchMode,
    definitions,
  });

  if (scopeCondition === undefined) {
    return userCondition;
  }
  if (userCondition === undefined) {
    return scopeCondition;
  }
  return { operator: "AND", conditions: [scopeCondition, userCondition] };
}
